import { useUiStore } from '@/stores/ui.store';
import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '@/stores/auth.store';
import { useStorage } from '@vueuse/core';
import sharedRoutes from '@/router/shared.routes';
import {
  BUSINESS_UNITS,
  BusinessUnitKey,
  getBusinessUnitConfig,
  getBusinessUnitRoutes,
} from '@/modules/bu/registry';
import { getBuFromDomain, getStoredBu } from '@/utils/bu.utils';

const buRoutes = BUSINESS_UNITS.map((bu) => ({
  path: bu.basePath,
  children: getBusinessUnitRoutes(bu.key),
}));

let pendingOffer: string | undefined =
  typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('offer') ?? undefined
    : undefined;

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'ChooseBusinessUnitView',
      component: () => import('@/views/common/ChooseBusinessUnitView.vue'),
      meta: { preserveOffer: true },
      beforeEnter: (to) => {
        const offer = typeof to.query.offer === 'string' ? to.query.offer : undefined;
        const domainBu = getBuFromDomain(window.location.origin);
        const domainConfig = getBusinessUnitConfig(domainBu);
        if (domainConfig) {
          if (offer) return { name: domainConfig.loginRouteName, query: { offer } };
          return { path: domainConfig.basePath, query: to.query };
        }

        const authStore = useAuthStore();
        if (authStore.access_token) {
          const storedBu = getStoredBu();
          const storedConfig = getBusinessUnitConfig(storedBu);
          if (storedConfig) {
            if (offer) return { name: storedConfig.loginRouteName, query: { offer } };
            return { path: storedConfig.basePath, query: to.query };
          }

          if (authStore.isCleepoAccount) {
            const config = getBusinessUnitConfig(BusinessUnitKey.Cleepo);
            if (config) {
              if (offer) return { name: config.loginRouteName, query: { offer } };
              return { path: config.basePath, query: to.query };
            }
          }
          if (authStore.isFlokeeAccount) {
            const config = getBusinessUnitConfig(BusinessUnitKey.Flokee);
            if (config) {
              if (offer) return { name: config.loginRouteName, query: { offer } };
              return { path: config.basePath, query: to.query };
            }
          }
        }
      },
    },
    ...buRoutes,
    ...sharedRoutes,
  ],
});

router.afterEach(() => {
  const uiStore = useUiStore();
  uiStore.setLoading(false);
});

// Preserve offer only within the registration flow to avoid leaking it elsewhere.
router.beforeEach((to, from) => {
  const shouldPreserveOffer = to.matched.some((record) => record.meta?.preserveOffer);
  const toOffer = typeof to.query.offer === 'string' ? to.query.offer : undefined;
  if (!shouldPreserveOffer) {
    pendingOffer = undefined;
    return;
  }

  if (toOffer) {
    pendingOffer = toOffer;
    return;
  }

  if (pendingOffer) {
    return {
      ...to,
      query: { ...to.query, offer: pendingOffer },
    };
  }
});

router.beforeEach((to, from) => {
  const lastPath = useStorage('app_last_path', '', sessionStorage);
  if (to.path === from.path) {
    return;
  }

  lastPath.value = from.path;
  return;
});

router.onError((error, to) => {
  if (
    error.message.includes('Failed to fetch dynamically imported module') ||
    error.message.includes('Importing a module script failed')
  ) {
    if (!to?.fullPath) {
      window.location.reload();
    } else {
      window.location.href = to.fullPath;
    }
  }
});

export default router;
